import { useEffect, useState, useRef, useMemo } from 'react';
import { INIT_ENCOUNTER_NAME, INIT_ENCOUNTER, buildNestedFolders, handleOpenSession, filterFolders, generateUniqueId } from '../../../constants';
import FolderList from './FolderList';
import { useUser } from '../../../../../../providers/UserProvider';
import magPlus from '../../../pics/icons/magPlus.PNG'
import magMinus from '../../../pics/icons/magMinus.PNG'
import OptionButton from '../OptionButton';

const DropdownMenu = ({adminView, streamingEncounter, savedEncounters, setSavedEncounters, handleLoadEncounter, currentEncounter, setCurrentEncounter, setStreamingEncounter, showMags = false, socket}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [collapsedFolders, setCollapsedFolders] = useState({});
    const [search, setSearch] = useState(""); // 1. Add search state
    const dropdownRef = useRef(null);
    const { username, sessionID, setSessionID, prefs} = useUser();
    
    // Build nested folder structure
    const nestedFolders = useMemo(() => buildNestedFolders(savedEncounters), [savedEncounters]);
    const filteredFolders = useMemo(() => filterFolders(nestedFolders, search), [nestedFolders, search]);

    useEffect(() => {
        if(username !== 'Username') {
            console.log("Signed in - Getting Session")
            socket.emit("getCurrentSession");
        } else {
            handleSetStreamingEncounter(null, null)
            setSessionID(null)
            setIsOpen(false)
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [username]);

    useEffect(() => {
        if(savedEncounters.length === 0) { 
            handleSetStreamingEncounter(null, null)
        } else if(sessionID && streamingEncounter?.encounterName === '') {
            savedEncounters.forEach(e => {
                if(e.encounterGuid === streamingEncounter.encounterGuid) {
                    handleSetStreamingEncounter(e.encounterGuid, e.encounterName)
                    console.log(`Found stream: ${e.encounterName} - (${e.encounterGuid})`)
                }
            });

        }
    }, [savedEncounters]);

    const iconSizeChange = (mathOp) => {
        socket.emit("iconSizeChange", mathOp, streamingEncounter.encounterGuid, prefs.useGlobalPvOptions, username)
    };

    useEffect(() => {
        const handlePlusMinus = (e) => {
            // Only trigger if not focusing an input, textarea, or contenteditable element
            const tag = document.activeElement.tagName.toLowerCase();
            const isEditable = document.activeElement.isContentEditable;
            if (tag === "input" || tag === "textarea" || isEditable) return;
            if (e.key === "=") {
                iconSizeChange("+");
            } else if (e.key === "-") {
                iconSizeChange("-");
            }
        };
        window.addEventListener("keydown", handlePlusMinus);
        return () => window.removeEventListener("keydown", handlePlusMinus);
    }, [streamingEncounter?.encounterGuid])

    useEffect(() => {
        if(socket) {
            // Recieve messages from backend
            socket.on('sendSession', (sessionInfo) => {
                setSessionID(sessionInfo.sessionID)
                // streamingEncounterGuid can be empty if nothing is found in the backend
                let log = "No Stream found..."
                savedEncounters.forEach(e => {
                    if(e.encounterGuid === sessionInfo.streamingEncounterGuid) {
                        handleSetStreamingEncounter(e.encounterGuid, e.encounterName)
                        log = `Found stream: ${e.encounterName} - (eGuid: ${e.encounterGuid})`
                    }
                });
                console.log(log)
            });
        }
    }, [socket]);

    function clickEncounterDropdownMenuX(event, encounter) {
        event.stopPropagation();
        let updatedEncounterList = savedEncounters.filter(e => e.encounterGuid !== encounter.encounterGuid)
        setSavedEncounters(updatedEncounterList);
        
        if(currentEncounter.encounterGuid === encounter.encounterGuid) {
            setCurrentEncounter({...INIT_ENCOUNTER, encounterGuid: generateUniqueId()})
        }
        
        socket.emit("deleteEncounter", encounter.encounterGuid)
        console.log("%cDeleted Encounter: " + encounter.encounterName + ' - ' + encounter.encounterGuid, "background: #fdfd96;")
    }

    const handleSetStreamingEncounter = (eGuid, eName, clicked = false) => {
        if(sessionID) {
            //if encounter is clicked
            if(eGuid !== null) {
                // if playerwindow is not open
                if(window.localStorage.getItem("playerViewOpen") === 'false' && clicked) {
                    handleOpenSession(sessionID)
                }
            }
        }

        if(setStreamingEncounter) {
            setStreamingEncounter({encounterGuid: eGuid, encounterName: eName})
        }
    }
    
    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setIsOpen(false);
        }
    };

    const handleDropDownOptionClicked = (encounter) => {
        setIsOpen(false);
        handleLoadEncounter(encounter);
        // set the last enecounter is local storage
        window.localStorage.setItem("lastEncounterGuid", encounter.encounterGuid)
    };

    const handleStreamButtonsClicked = (e, enc, type, openSessionTab=false) => {
        e.stopPropagation();
        if(type === "stop") {
            socket.emit("stopStreaming", enc.encounterGuid, enc.encounterName, sessionID)
            handleSetStreamingEncounter(null, null, false)
        } else if(enc.encounterGuid !== streamingEncounter.encounterGuid) {
            socket.emit("startStreaming", enc.encounterGuid, enc.encounterName, sessionID)
            handleSetStreamingEncounter(enc.encounterGuid, enc.encounterName, true)
        } 

        if(openSessionTab)
            handleOpenSession(sessionID)

    };

    let buttonString = "No Encounters"
    if(savedEncounters?.length !== 0) {
        buttonString = currentEncounter.encounterName === INIT_ENCOUNTER_NAME ? "Encounters..." : "Encounter: "
    }

    // let buttonString = streamingEncounter.encounterGuid === null ? `Stream Player View...` : "Streaming "
    // if(username === 'Username') {
    //     buttonString = "Sign in to stream"
    // }

    return (
        <div className="dropdown" ref={dropdownRef}>
            <button className="dmViewButton dropdownButton" onClick={() => setIsOpen(!isOpen)} disabled={savedEncounters.length === 0}>
                {buttonString}
                {currentEncounter && currentEncounter.encounterName !== INIT_ENCOUNTER_NAME && 
                    <p style={{margin: 0, textWrap: 'nowrap'}}>
                        <b>{currentEncounter.encounterName}</b>
                    </p>
                }
            </button>
            {isOpen && savedEncounters.length !== 0 && (
                <div>
                    <ul className="dropdownMenu" style={{
                        maxHeight: currentEncounter.encounterGuid ? "55vh" : "25vh",
                        borderTop: "2px solid rgb(105, 19, 19)",
                        paddingLeft: 0,
                        overflowX: 'hidden'
                    }}>
                        <input id='encounterSearch' type="text" placeholder="Search Encounters/Folders" value={search} onChange={e => setSearch(e.target.value)} style={{ width: '97%', padding: "4px 0px 4px 2px", margin: '0',border: "2px solid rgb(157, 125, 72)", fontSize: "0.8em"}} autoFocus={true} />
                        <div style={{background: "white", padding: '0 12px 0 12px', borderBottom: '2px solid black', fontSize: '0.75em'}}> 
                            {sessionID &&
                                <>
                                    <li className='dropdown-item' style={{justifyContent: 'center'}} onClick={() => {handleOpenSession(sessionID)}}>
                                        <strong>Open PlayerView</strong> 📺
                                    </li>
                                    <li className='dropdown-item' style={{justifyContent: 'center'}} onClick={() => {handleOpenSession(sessionID, true)}}>
                                        <strong>Open Tv-Table View</strong> 📺
                                    </li>
                                </>
                            }

                            {streamingEncounter?.encounterGuid !== null && (
                                <li className="dropdown-item" onClick={(e) => {handleStreamButtonsClicked(e, streamingEncounter, "stop", false)}} style={{justifyContent: 'center', background: "white", textDecoration: 'underline', textDecorationThickness: '2px', textDecorationColor: 'red'}}>
                                    Stop Streaming
                                </li>
                            )}
                        </div>
                        <FolderList
                            folders={filteredFolders}
                            onEncounterClick={handleDropDownOptionClicked}
                            streamingEncounter={streamingEncounter}
                            adminView={adminView}
                            clickEncounterDropdownMenuX={clickEncounterDropdownMenuX}
                            currentEncounter={currentEncounter}
                            setCurrentEncounter={setCurrentEncounter}
                            collapsedFolders={collapsedFolders}
                            setCollapsedFolders={setCollapsedFolders}
                            handleStartStream={handleStreamButtonsClicked}
                        />
                    </ul>
                </div>
            )}
            {showMags && sessionID && streamingEncounter?.encounterGuid !== null &&
                <div className='magWrapper'>
                    <OptionButton imgClassName={'magOptions'} src={magPlus} message={<>Grow Icons <span style={{fontSize: "12px", fontFamily: "Courier New"}}>(Shortcut '=')</span></>} onClickFunction={()=> iconSizeChange("+")}/>
                    <OptionButton imgClassName={'magOptions'} src={magMinus} message={<>Shrink Icons <span style={{fontSize: "12px", fontFamily: "Courier New"}}>(Shortcut '-')</span></>} onClickFunction={()=> iconSizeChange("-")}/>
                </div>
            }
        </div>
    );
};

export default DropdownMenu;